import React from 'react';
import { Button, Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CourierFilterCached, FormProvider, RHFSelectField, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form'
import _ from 'lodash';

const paymentModes = [{ label: 'Prepaid', value: 'PREPAID' }, { label: 'COD', value: 'COD' }]
const zoneOptions = [
    { label: 'Local', value: 'LOCAL' },
    { label: 'Metro', value: 'METROS' },
    { label: 'Regional (Within State)', value: 'REGIONAL' },
    { label: 'Rest of India (ROI)', value: 'ROI' },
    { label: 'Special', value: 'SPECIAL' },
]
const defaultFilter = { paymentMode: '', pickupPincode: '', courier: '', merchantZone: '' }

// Read a courier pre-filter from the URL (?courier=<accountId>) — used when arriving
// from Courier Health's "View" so the detail opens scoped to that courier.
const courierFromUrl = (search) => new URLSearchParams(search || '').get('courier') || ''

export const FilterContext = React.createContext()

// Apply the shared analytics filters to a row set. `courier` may be a single
// account id OR several comma-joined ids (a parent → all of its accounts), so it
// is matched as set-membership against each row's `courier`; every other filter
// matches exactly (lodash predicate). Use this in analytics hooks instead of a
// bare `_.filter(rows, filters)` so parent-level courier selection works.
export function applyCourierFilters(rows, filters) {
    const { courier, ...rest } = filters || {}
    let out = _.filter(rows || [], rest)
    const ids = courier ? String(courier).split(',').filter(Boolean) : []
    if (ids.length) {
        out = out.filter((r) => ids.includes(r.courier))
    }
    return out
}

export function useGetSelectedFilters() {
    let { filters } = React.useContext(FilterContext)

    if (filters) {
        for (const k of Object.keys(filters)) {
            if (_.isEmpty(filters[k])) {
                delete filters[k]
            }
        }
    }

    return filters;
}

export default React.memo(({ hideFilters = [] }) => {

    const { setFilters } = React.useContext(FilterContext)
    const { search } = useLocation()

    const methods = useForm({ defaultValues: { ...defaultFilter, courier: courierFromUrl(search) } })
    const { handleSubmit } = methods

    const onFilterSubmit = React.useCallback((filters) => {
        setFilters(filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onFilterSubmit)}>
            <Grid container spacing={1.5} alignItems="center">
                {!hideFilters.includes("paymentMode") && (
                    <Grid item xs>
                        <RHFSelectField enableNoneSelect name="paymentMode" options={paymentModes} width='100%' label="Payment Mode" />
                    </Grid>
                )}
                {!hideFilters.includes("courier") && (
                    <Grid item xs>
                        <CourierFilterCached enableNoneSelect />
                    </Grid>
                )}
                {!hideFilters.includes("pickupPincode") && (
                    <Grid item xs>
                        <RHFTextField name="pickupPincode" label="Pickup Pincode" />
                    </Grid>
                )}
                {!hideFilters.includes("zone") && (
                    <Grid item xs>
                        <RHFSelectField enableNoneSelect name="merchantZone" options={zoneOptions} width='100%' label="Zone" />
                    </Grid>
                )}
                <Grid item xs="auto">
                    <Button variant='contained' color='primary' size="small" type="submit">Apply</Button>
                </Grid>
            </Grid>
        </FormProvider>
    )
})

export function FiltersContextProvider({ children }) {
    const { search } = useLocation()
    const initialCourier = courierFromUrl(search)
    const [filters, setFilters] = React.useState(initialCourier ? { courier: initialCourier } : undefined)

    return (
        <FilterContext.Provider value={{ filters, setFilters }}>
            {children}
        </FilterContext.Provider>
    )
}